import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Align = 'start' | 'end';

/**
 * Anchors a portaled popover panel to a trigger element.
 *
 * The analytics toolbar is sticky with its own stacking context (z-index +
 * backdrop-filter), which traps absolutely-positioned children — a dropdown's
 * z-index only competes *inside* the toolbar, so on some viewports it renders
 * behind page content. Rendering the panel in a portal to document.body with
 * `position: fixed` escapes that trap entirely; it always paints on top.
 *
 * Returns a ref for the panel element, the fixed-position style to spread onto
 * it (overriding the panel CSS's `position: absolute`), and the portal target.
 *
 * The portal target is the nearest `.av2-page` ancestor, NOT document.body:
 * the design tokens (--av2-*) are scoped to `.av2-page`, so a panel under
 * document.body would lose all var()-based styling. `.av2-page` is
 * position:relative (not transformed), so a position:fixed child stays
 * viewport-anchored and isn't clipped by its `overflow-x: clip` — we keep the
 * tokens and still escape the toolbar's stacking context.
 *
 * Also owns click-outside + Escape close, and repositions on scroll/resize.
 */
export function useAnchoredPopover(
    anchorRef: React.RefObject<HTMLElement>,
    open: boolean,
    onClose: () => void,
    align: Align = 'start',
    // When the panel CSS relies on `min-width: 100%` (sized to the trigger
    // while position:absolute), set this so we pin minWidth to the trigger's
    // pixel width — otherwise `100%` resolves to the viewport once fixed.
    matchAnchorWidth = false,
) {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [style, setStyle] = useState<React.CSSProperties>({});

    const reposition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const r = anchor.getBoundingClientRect();
        const margin = 8;
        const next: React.CSSProperties = {
            position: 'fixed',
            top: r.bottom + 6,
            zIndex: 4000,
        };
        // Anchor to one edge of the trigger, and cap maxWidth toward the
        // opposite viewport edge so the panel never spills off-screen on
        // narrow (mobile) viewports. On desktop the cap is large = no effect.
        if (align === 'end') {
            next.right = Math.max(margin, window.innerWidth - r.right);
            next.maxWidth = Math.max(0, r.right - margin);
        } else {
            next.left = Math.max(margin, r.left);
            next.maxWidth = Math.max(0, window.innerWidth - (next.left as number) - margin);
        }
        if (matchAnchorWidth) next.minWidth = r.width;
        setStyle(next);
    }, [anchorRef, align, matchAnchorWidth]);

    useLayoutEffect(() => {
        if (!open) return;
        reposition();
        const onMove = () => reposition();
        // capture:true so the analytics page's own scroll container is caught too
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [open, reposition]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (anchorRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, anchorRef, onClose]);

    // Portal into the .av2-page root (token scope) rather than document.body.
    // anchorRef is mounted whenever a popover opens, so closest() resolves.
    const portalTarget: HTMLElement | null =
        (anchorRef.current?.closest('.av2-page') as HTMLElement | null) ??
        (typeof document !== 'undefined' ? document.body : null);

    return { panelRef, style, portalTarget };
}
